// Production sheet domain model shared by the grid, dispatch panel and PDF preview.
export const SECTIONS = [
  { key: "cabinet_boxes", label: "Cabinet Boxes", short: "Boxes" },
  { key: "doors_fronts", label: "Doors / Fronts", short: "Doors" },
  { key: "gables_mouldings", label: "Gables / Mouldings", short: "Gables" },
];

export const MATERIALS = [
  { value: "maple", label: "Maple" },
  { value: "mdf", label: "MDF" },
  { value: "hpl", label: "HPL" },
  { value: "thermofoil", label: "Thermofoil" },
  { value: "plywood", label: "Plywood" },
  { value: "melamine", label: "Melamine" },
];
export const FACED = new Set(["maple", "mdf"]);
export const PAD_IN = 2;

const TYPES = {
  cabinet_boxes: ["base", "wall", "tall", "vanity", "island", "other"],
  doors_fronts: ["door", "drawer_front", "panel", "other"],
  gables_mouldings: ["gable", "moulding", "filler", "toe_kick", "valance", "other"],
};

export const parseMeasurement = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  const s = String(value).trim().replace(/"/g, "");
  let m = s.match(/^(\d+(?:\.\d+)?)[\s-]+(\d+)\/(\d+)$/);
  if (m) return parseFloat(m[1]) + (Number(m[3]) ? Number(m[2]) / Number(m[3]) : 0);
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return Number(m[2]) ? Number(m[1]) / Number(m[2]) : 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

// (((w+2)*(h+2))/144)*qty, doubled for 2-face. Production/finishing only — never customer pricing.
export const finishingSqft = (w, h, qty, faces) => {
  const W = parseMeasurement(w), H = parseMeasurement(h);
  if (W <= 0 || H <= 0) return 0;
  return ((W + PAD_IN) * (H + PAD_IN) / 144) * (Number(qty) || 0) * (Number(faces) === 2 ? 2 : 1);
};

export const rowSqft = (row) => (FACED.has(row.material) ? finishingSqft(row.width, row.height, row.quantity, row.faces || 1) : 0);

// Maple -> stain / 2 faces (locked). MDF -> painted, 1|2 faces. Everything else -> no face toggle.
export const applyMaterialRules = (row) => {
  const r = { ...row };
  if (r.material === "maple") { r.finish = "stained"; r.faces = 2; }
  else if (r.material === "mdf") { r.finish = "painted"; r.faces = Number(r.faces) === 2 ? 2 : 1; }
  else { r.finish = ["hpl", "thermofoil"].includes(r.material) ? r.material : null; r.faces = null; }
  return r;
};

export const newRow = (section) => applyMaterialRules({
  row_id: `new_${Math.random().toString(36).slice(2, 10)}`, section, item_code: "", description: "", item_type: TYPES[section][0],
  width: "", height: "", depth: "", quantity: 1, material: section === "cabinet_boxes" ? "plywood" : "mdf", faces: 1, notes: "", dispatched_to: [],
});

const typeCol = (section) => ({ key: "item_type", label: "Type", type: "select", width: 110, options: TYPES[section].map(t => ({ value: t, label: t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) })) });
const matCol = { key: "material", label: "Material", type: "select", width: 110, options: MATERIALS };
const finishCol = { key: "finish", label: "Finish", type: "computed", width: 90, display: (r) => (FACED.has(r.material) ? (r.finish === "stained" ? "Stained" : "Painted") : "—") };
const facesCol = { key: "faces", label: "Faces", type: "select", width: 80, readOnly: (r) => r.material !== "mdf",
  options: [{ value: 1, label: "1-face" }, { value: 2, label: "2-face" }], display: (r) => (FACED.has(r.material) ? `${r.faces}-face` : "—"), cast: Number };
const sqftCol = { key: "sqft", label: "Paint SqFt", type: "computed", width: 90, display: (r) => (rowSqft(r) > 0 ? rowSqft(r).toFixed(2) : "—") };
const qtyCol = { key: "quantity", label: "Qty", type: "number", width: 64 };
const notesCol = { key: "notes", label: "Notes", type: "text", width: 180 };
const productCodeCol = { key: "item_code", label: "Product Code", type: "text", width: 126 };
const descCol = { key: "description", label: "Item Description", type: "text", width: 210 };

export const columnsFor = (section) => {
  if (section === "cabinet_boxes") return [
    productCodeCol, descCol, typeCol(section),
    { key: "width", label: 'W"', type: "measure", width: 76 }, { key: "height", label: 'H"', type: "measure", width: 76 }, { key: "depth", label: 'D"', type: "measure", width: 76 },
    matCol, qtyCol, notesCol];
  const dims = section === "doors_fronts"
    ? [{ key: "width", label: 'W"', type: "measure", width: 76 }, { key: "height", label: 'H"', type: "measure", width: 76 }]
    : [{ key: "width", label: 'L"', type: "measure", width: 76 }, { key: "height", label: 'W"', type: "measure", width: 76 }];
  return [productCodeCol, descCol, typeCol(section), ...dims, matCol, finishCol, facesCol, sqftCol, qtyCol, notesCol];
};

export const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  partially_dispatched: { label: "Partially dispatched", cls: "bg-amber-100 text-amber-800" },
  dispatched: { label: "Dispatched", cls: "bg-emerald-100 text-emerald-800" },
};
